import { ReactNode } from "react";

import { Box, LinearProgress, linearProgressClasses, Typography } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
    ...theme.applyStyles("dark", {
      backgroundColor: theme.palette.grey[800],
      width: "100px",
    }),
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: "#1a90ff",
    ...theme.applyStyles("dark", {
      backgroundColor: "#308fe8",
      width: "100px",
    }),
  },
}));

export interface ProgressIndicatorProps {
  /** Valor do progresso (0-100) */
  value: number;
  /** Título principal exibido acima da barra de progresso */
  title: string;
  /** Texto informativo abaixo da barra de progresso */
  subtitle?: string;
  /** Texto adicional secundário */
  additionalText?: string;
  /** Mostrar o CircularProgress animado (default: true) */
  showCircular?: boolean;
  /** Largura da barra de progresso (default: "300px") */
  width?: string | number;
  /** Variante do progresso: determinate mostra valor, indeterminate mostra animação contínua */
  variant?: "determinate" | "indeterminate";
  /** Conteúdo adicional (ex: botões de ação) */
  children?: ReactNode;
}

const styleTitle = {
  fontSize: 16,
  fontWeight: "500",
};

const styleBody = {
  fontSize: 14,
  fontWeight: "500",
};

const ProgressIndicator = ({
  value,
  title,
  subtitle,
  additionalText,
  showCircular = true,
  width = "300px",
  variant = "determinate",
  children,
}: ProgressIndicatorProps) => {
  return (
    <>
      {showCircular && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 5,
            textAlign: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <Typography color="primary" style={styleTitle}>
        {title}
      </Typography>

      <BorderLinearProgress
        sx={{ width, margin: "5px" }}
        variant={variant}
        value={value}
      />

      {subtitle && (
        <Typography variant="body1" color="var(--blue-gray-600)" style={styleBody}>
          {subtitle}
        </Typography>
      )}

      {additionalText && (
        <Typography variant="body1" color="var(--blue-gray-300)" style={styleBody}>
          {additionalText}
        </Typography>
      )}

      {children}
    </>
  );
};

export default ProgressIndicator;
